import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mail,
  Clock,
  Check,
  X,
  AlertCircle,
  Users,
  Calendar,
  Building2,
  Loader
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { handleApiError } from '@/lib/utils'

interface TeamInvitation {
  id: number
  invited_email: string
  team_id: number
  team_name: string
  invitation_message: string
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled'
  created_at: string
  expires_at: string
  responded_at?: string
  response_message?: string
  invited_by_name: string
  invitation_token: string
}

interface PendingInvitationsProps {
  refreshTrigger?: number
  onInvitationResponse?: () => void
}

export default function PendingInvitations({ refreshTrigger, onInvitationResponse }: PendingInvitationsProps) {
  const [invitations, setInvitations] = useState<TeamInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [responding, setResponding] = useState<number | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    loadPendingInvitations()
  }, [refreshTrigger])

  const loadPendingInvitations = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await apiClient.getTeamInvitations('received')
      
      if (response.success && response.data) {
        const pendingInvitations = (response.data as any).invitations?.filter(
          (inv: TeamInvitation) => inv.status === 'pending'
        ) || []
        setInvitations(pendingInvitations)
      }
    } catch (err) {
      setError('Failed to load invitations: ' + handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptInvitation = async (invitation: TeamInvitation) => {
    try {
      setResponding(invitation.id)
      const response = await apiClient.acceptTeamInvitation(
        invitation.invitation_token,
        `Excited to join ${invitation.team_name}!`
      )

      if (response.success) {
        // Remove from pending list
        setInvitations(prev => prev.filter(inv => inv.id !== invitation.id))
        onInvitationResponse?.()
        
        // You might want to show a success toast here
        console.log(`Successfully joined team: ${invitation.team_name}`)
      } else {
        setError(response.message || 'Failed to accept invitation')
      }
    } catch (err) {
      setError('Failed to accept invitation: ' + handleApiError(err))
    } finally {
      setResponding(null)
    }
  }

  const handleDeclineInvitation = async (invitation: TeamInvitation) => {
    try {
      setResponding(invitation.id)
      const response = await apiClient.declineTeamInvitation(
        invitation.invitation_token,
        'Thank you for the invitation, but I cannot join at this time.'
      )

      if (response.success) {
        // Remove from pending list
        setInvitations(prev => prev.filter(inv => inv.id !== invitation.id))
        onInvitationResponse?.()
        
        console.log(`Declined invitation to: ${invitation.team_name}`)
      } else {
        setError(response.message || 'Failed to decline invitation')
      }
    } catch (err) {
      setError('Failed to decline invitation: ' + handleApiError(err))
    } finally {
      setResponding(null)
    }
  }

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d ago`
    
    return formatDate(dateString)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center">
          <Loader className="w-5 h-5 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-600">Loading invitations...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
        <div className="flex items-center space-x-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!invitations.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
        <Mail className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-gray-900 mb-1">No Pending Invitations</h3>
        <p className="text-sm text-gray-500">You don't have any team invitations at the moment.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Mail className="w-5 h-5 mr-2 text-blue-600" />
          Pending Invitations ({invitations.length})
        </h3>
      </div>

      <AnimatePresence>
        {invitations.map((invitation) => (
          <motion.div
            key={invitation.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`bg-white rounded-lg shadow-sm border-l-4 border-blue-400 p-6 ${
              isExpired(invitation.expires_at) ? 'opacity-60' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <h4 className="text-lg font-medium text-gray-900">{invitation.team_name}</h4>
                  {isExpired(invitation.expires_at) && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <Clock className="w-3 h-3 mr-1" />
                      Expired
                    </span>
                  )}
                </div>

                <div className="text-sm text-gray-600 space-y-1 mb-4">
                  <div className="flex items-center">
                    <Building2 className="w-4 h-4 mr-2" />
                    <span>Invited by {invitation.invited_by_name}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>Sent {formatTimeAgo(invitation.created_at)} • Expires {formatDate(invitation.expires_at)}</span>
                  </div>
                </div>

                {invitation.invitation_message && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-gray-700 italic">"{invitation.invitation_message}"</p>
                  </div>
                )}
              </div>
            </div>

            {!isExpired(invitation.expires_at) && (
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleDeclineInvitation(invitation)}
                  disabled={responding === invitation.id}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {responding === invitation.id ? (
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <X className="w-4 h-4 mr-2" />
                  )}
                  Decline
                </button>

                <button
                  onClick={() => handleAcceptInvitation(invitation)}
                  disabled={responding === invitation.id}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {responding === invitation.id ? (
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Accept & Join Team
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}